import axios from 'axios'
import { getClerkToken } from '@/lib/auth'
import { currentHousehold } from '@/lib/currentHousehold'
import { queryClient } from '@/lib/queryClient'
import { ROUTES } from '@/routes/routes'

export const apiBaseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:8080'

const api = axios.create({
  baseURL: `${apiBaseUrl}/api/v1`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

api.interceptors.request.use(async (config) => {
  const token = await getClerkToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  const householdId = currentHousehold.get()
  if (householdId) {
    config.headers['X-Household-Id'] = householdId
  }
  return config
})

// Guards against every in-flight query firing its own redirect at once (a stale/expired session
// typically fails several requests within the same tick).
let redirectingToSignIn = false

// A 401 while already on a public auth page doesn't mean the session expired — it means there
// never was one (e.g. a request firing before the user has signed in at all). Redirecting to
// sign-in from sign-in via a full page reload would just re-fire the same request and loop
// forever. React Query hooks should avoid making authenticated calls before sign-in in the
// first place (see useCurrentUser.ts) — this is a second line of defense, not the primary fix.
function onPublicPage(): boolean {
  if (window.location.pathname === '/') return true
  return Object.values(ROUTES.AUTH).some(
    (path) => window.location.pathname === path || window.location.pathname.startsWith(path + '/')
  )
}

// A 401 here means the Clerk session itself is gone (expired, revoked, or the tab was left open
// long enough that the token Clerk cached went stale) — react-query would otherwise just render
// each affected query's own "Failed to load" error state forever, with no path back to sign-in.
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const config = err?.config
    if (err?.response?.status === 401 && !onPublicPage()) {
      // Clerk itself still thinks there's an active session — this 401 is more likely the request
      // interceptor racing ahead of Clerk finishing setActive() (e.g. the very first request right
      // after sign-in) than an actual expired/revoked session. Retry once with a freshly-fetched
      // token before treating it as a real logout.
      if (window.Clerk?.session && config && !config.__retriedAfterAuthRace) {
        config.__retriedAfterAuthRace = true
        const token = await window.Clerk.session.getToken({ skipCache: true }).catch(() => null)
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
          return api.request(config)
        }
      }

      if (!redirectingToSignIn) {
        redirectingToSignIn = true
        const redirectTo = encodeURIComponent(window.location.pathname + window.location.search)
        currentHousehold.clear()
        queryClient.clear()
        await window.Clerk?.signOut?.().catch(() => {})
        window.location.href = `${ROUTES.AUTH.SIGN_IN}?redirectTo=${redirectTo}`
      }
    }
    return Promise.reject(err)
  }
)

export default api
